/**
 * Shared transform: ShipmentResponse (API) → Container (UI).
 * Used by container detail page and Live Tracking detail overlay for consistency.
 */

import type { Container, ContainerStatus, DocumentStatus } from "@/types/container";
import type { ShipmentResponse } from "@/hooks/api/use-containers-recap";
import { parseUtcISO } from "@/utils/date-formatters";

function riskScoreToLevel(score: number | null): Container["riskLevel"] {
    if (score === null) return "Low";
    if (score <= 0.25) return "Low";
    if (score <= 0.5) return "Medium";
    if (score <= 0.75) return "High";
    return "Critical";
}

function mapApiStatusToContainerStatus(status: string): ContainerStatus {
    const statusMap: Record<string, ContainerStatus> = {
        draft: "Draft",
        booked: "Booked",
        in_transit: "In Transit",
        arrived: "Arrived",
        delivered: "Delivered",
        on_hold: "On Hold",
        cancelled: "Cancelled",
        booking_confirmed: "Booked",
        documents_pending: "Booked",
        ready_to_ship: "Booked",
        arrived_at_port: "Arrived",
        customs_clearance: "Arrived",
        customs_cleared: "Arrived",
        out_for_delivery: "Delivered",
        completed: "Delivered",
    };
    return statusMap[status.toLowerCase()] || "Draft";
}

export function transformShipmentToContainer(shipment: ShipmentResponse): Container {
    const riskLevel = riskScoreToLevel(shipment.risk_score);
    const status = mapApiStatusToContainerStatus(shipment.status);

    let documentStatus: DocumentStatus = "Pending";
    if (shipment.documents_complete && shipment.documents_validated) {
        documentStatus = "Completo";
    } else if (shipment.documents_complete && !shipment.documents_validated) {
        documentStatus = "Error";
    }

    const waypoints: Container["waypoints"] = [];
    if (shipment.origin_port_lat != null && shipment.origin_port_lon != null) {
        waypoints.push({
            id: "origin",
            lat: shipment.origin_port_lat,
            lng: shipment.origin_port_lon,
            label: shipment.origin_port_name || shipment.origin_country_code || "Origin",
            type: "start",
            info: {
                portName: shipment.origin_port_name || undefined,
                country: shipment.origin_country_code || undefined,
                status: "planned",
            },
        });
    }
    if (shipment.destination_port_lat != null && shipment.destination_port_lon != null) {
        waypoints.push({
            id: "destination",
            lat: shipment.destination_port_lat,
            lng: shipment.destination_port_lon,
            label: shipment.destination_port_name || shipment.destination_country_code || "Destination",
            type: "end",
            info: {
                portName: shipment.destination_port_name || undefined,
                country: shipment.destination_country_code || undefined,
                eta: shipment.estimated_arrival_date || undefined,
                status: "planned",
            },
        });
    }

    return {
        id: shipment.container_id,
        containerNumber: shipment.container_number || undefined,
        status,
        riskLevel,
        documentStatus,
        progress: shipment.progress_percentage ?? 0,
        route: {
            from: shipment.origin_port_name || shipment.origin_country_code || "N/A",
            to: shipment.destination_port_name || shipment.destination_country_code || "N/A",
        },
        eta: shipment.estimated_arrival_date ? new Date(shipment.estimated_arrival_date) : new Date(),
        shipperId: shipment.shipper_company_id || "",
        receiverId: shipment.consignee_company_id || "",
        shippingCompany: shipment.carrier_name || undefined,
        value: {
            amount: shipment.total_value_usd ?? 0,
            currency: shipment.currency || "USD",
        },
        createdAt: new Date(shipment.created_at),
        updatedAt: new Date(shipment.updated_at),
        waypoints,
        commodity: "avocado",
        bookingNumber: shipment.booking_number || undefined,
        blNumber: shipment.bl_number || undefined,
        purchaseOrderNumber: shipment.purchase_order_number || undefined,
        serviceContractNumber: shipment.service_contract_number || undefined,
        vesselName: shipment.vessel_name || undefined,
        voyageNumber: shipment.voyage_number || undefined,
        incoterms: shipment.incoterms || undefined,
        cargoType: shipment.cargo_type || undefined,
        transportMode: shipment.transport_mode || undefined,
        containerType: shipment.container_type || undefined,
        containerSize: shipment.container_size || undefined,
        isHazardous: shipment.is_hazardous,
        hazardClass: shipment.hazard_class || undefined,
        totalWeightKg: shipment.total_weight_kg ?? undefined,
        totalVolumeCbm: shipment.total_volume_cbm ?? undefined,
        totalPackages: shipment.total_packages ?? undefined,
        cargoDescription: shipment.cargo_description || undefined,
        hsCodePrimary: shipment.hs_code_primary || undefined,
        totalValueUsd: shipment.total_value_usd ?? undefined,
        freightChargesUsd: shipment.freight_charges_usd ?? undefined,
        insuranceValueUsd: shipment.insurance_value_usd ?? undefined,
        requiresTemperatureControl: shipment.requires_temperature_control,
        temperatureSetpointCelsius: shipment.temperature_setpoint_celsius ?? undefined,
        bookingDate: shipment.booking_date ? new Date(shipment.booking_date) : undefined,
        estimatedDepartureDate: shipment.estimated_departure_date ? new Date(shipment.estimated_departure_date) : undefined,
        actualDepartureDate: shipment.actual_departure_date ? new Date(shipment.actual_departure_date) : undefined,
        actualArrivalDate: shipment.actual_arrival_date ? new Date(shipment.actual_arrival_date) : undefined,
        estimatedDeliveryDate: shipment.estimated_delivery_date ? new Date(shipment.estimated_delivery_date) : undefined,
        lastTrackedAt: parseUtcISO(shipment.last_tracked_at),
        trackingProvider: shipment.tracking_provider || undefined,
        lastEmailUpdateAt: parseUtcISO(shipment.last_email_update_at),
        sourceEmailMessageId: shipment.source_email_message_id || undefined,
        alertCount: shipment.alert_count,
        hasActiveAlerts: shipment.has_active_alerts,
        specialInstructions: shipment.special_instructions || undefined,
        customerNotes: shipment.customer_notes || undefined,
        consigneeCompanyId: shipment.consignee_company_id || undefined,
    };
}
